import React, { useEffect, useState } from "react";
import { BookOpen, Plus, Trash2, Pencil, Layers, X, History, ChefHat, Archive, Upload } from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button, Modal, Select, EmptyState } from "../lib/ui";
import { useToast } from "../lib/toast";
import { fmtRp, fmtNum, fmtUnit } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import MenuImportWizard, { MenuImportHistory } from "../components/MenuImportWizard";

export default function Recipes() {
  const toast = useToast();
  const { isRole } = useAuth();
  const { t } = useT();
  const [items, setItems] = useState(null);
  const [products, setProducts] = useState([]);
  const [itemModal, setItemModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", category: "", price: "" });
  const [recipeFor, setRecipeFor] = useState(null);
  const [recipe, setRecipe] = useState(null);
  const [lines, setLines] = useState([]);
  const [advanced, setAdvanced] = useState(false);
  const [versions, setVersions] = useState(null);
  const [busy, setBusy] = useState(false);

  const canEdit = isRole("SUPER_ADMIN", "OWNER", "CENTRAL_PURCHASING");
  const canImport = isRole("SUPER_ADMIN", "OWNER");
  const [importOpen, setImportOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = () => api.get("/menu-items").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    api.get("/products?active_only=true&visible_in=recipe_bom").then(({ data }) => setProducts(data.filter((p) => p.track_inventory)));
  }, []);

  const openItemModal = (it = null) => {
    setEditItem(it);
    setForm(it ? { name: it.name, code: it.code || "", category: it.category || "", price: it.price ?? "" } : { name: "", code: "", category: "", price: "" });
    setItemModal(true);
  };

  const saveItem = async () => {
    if (!form.name.trim()) return toast.error(t("Name is required"));
    setBusy(true);
    try {
      const payload = { ...form, price: Number(form.price) || 0 };
      if (editItem) await api.put(`/menu-items/${editItem.id}`, payload);
      else await api.post("/menu-items", payload);
      toast.success(editItem ? t("Menu item updated") : t("Menu item created"));
      setItemModal(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setBusy(false); }
  };

  const removeItem = async (it) => {
    if (!window.confirm(`${t("Delete/archive")} "${it.name}"?`)) return;
    try { const { data } = await api.delete(`/menu-items/${it.id}`); toast.success(data.archived ? t("Archived (has sales)") : t("Deleted")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };

  const openRecipe = async (it) => {
    setRecipeFor(it); setRecipe(null); setVersions(null); setAdvanced(false);
    const { data } = await api.get(`/menu-items/${it.id}/recipe`);
    setRecipe(data);
    setLines(data.lines.length ? data.lines.map((l) => ({ product_id: l.product_id, quantity: l.quantity, yield_pct: l.yield_pct ?? 100, prep_loss_pct: l.prep_loss_pct ?? 0 })) : [{ product_id: "", quantity: "", yield_pct: 100, prep_loss_pct: 0 }]);
  };

  const recipeCost = lines.reduce((sum, l) => {
    const p = products.find((x) => x.id === l.product_id);
    return sum + (p ? (Number(l.quantity) || 0) * (p.price || 0) : 0);
  }, 0);

  const saveRecipe = async () => {
    const clean = lines.filter((l) => l.product_id && Number(l.quantity) > 0)
      .map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity), yield_pct: Number(l.yield_pct) || 100, prep_loss_pct: Number(l.prep_loss_pct) || 0 }));
    if (!clean.length) return toast.error(t("Add at least one ingredient with a quantity"));
    setBusy(true);
    try {
      const { data } = await api.post(`/menu-items/${recipeFor.id}/recipe`, { lines: clean });
      toast.success(`${t("Recipe saved")} (v${data.version})`);
      setRecipeFor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setBusy(false); }
  };

  const loadVersions = async () => {
    const { data } = await api.get(`/menu-items/${recipeFor.id}/recipe/versions`);
    setVersions(data);
  };

  if (!items) return <PageLoader />;

  return (
    <div className="animate-fade-up pb-24 lg:pb-2">
      <PageHeader title={t("Recipes & BOM")} subtitle={t("Menu items and their ingredient recipes (versioned)")} icon={BookOpen}>
        {canImport && <Button variant="outline" onClick={() => setHistoryOpen(true)} data-testid="menu-import-history-button"><History size={18} /> {t("Import History")}</Button>}
        {canImport && <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="menu-import-button"><Upload size={18} /> {t("Import Menu")}</Button>}
        {canEdit && <Button onClick={() => openItemModal()} data-testid="add-menu-item-button"><Plus size={18} /> {t("Menu Item")}</Button>}
      </PageHeader>

      {items.length === 0 ? (
        <EmptyState title={t("No menu items yet")} subtitle={t("Create sellable menu items and define their recipes to power consumption & variance analysis.")} />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {items.map((m) => (
            <Card key={m.id} className="flex flex-col p-4" data-testid={`menu-item-${m.name}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-light text-primary"><ChefHat size={18} /></div>
                  <div>
                    <p className="font-heading font-bold text-ink">{m.name}</p>
                    <p className="text-[11px] text-ink-mute">{m.code || "—"} · {fmtRp(m.price)}</p>
                  </div>
                </div>
                {m.has_recipe ? <Badge tone="success">{t("Recipe")} v{m.recipe_version}</Badge> : <Badge tone="warn">{t("No recipe")}</Badge>}
              </div>
              <p className="mt-3 text-xs text-ink-soft">{m.recipe_lines} {t("ingredient(s)")}{m.active === false && ` · ${t("archived")}`}</p>
              <div className="mt-3 flex flex-wrap gap-2 border-t border-line pt-3">
                <Button size="sm" onClick={() => openRecipe(m)} data-testid={`edit-recipe-${m.name}`}><Layers size={14} /> {canEdit ? (m.has_recipe ? t("Edit Recipe") : t("Add Recipe")) : t("View Recipe")}</Button>
                {canEdit && <Button size="sm" variant="outline" onClick={() => openItemModal(m)} data-testid={`edit-item-${m.name}`}><Pencil size={14} /></Button>}
                {canEdit && <Button size="sm" variant="outline" onClick={() => removeItem(m)} data-testid={`delete-item-${m.name}`}><Archive size={14} /></Button>}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Menu item modal */}
      <Modal open={itemModal} onClose={() => setItemModal(false)} title={editItem ? t("Edit Menu Item") : t("New Menu Item")}
        footer={<><Button variant="outline" onClick={() => setItemModal(false)}>{t("Cancel")}</Button><Button onClick={saveItem} loading={busy} data-testid="save-menu-item">{t("Save")}</Button></>}>
        <div className="space-y-3">
          <div><label className="cf-label">{t("Name")}</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="cf-input" data-testid="menu-item-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="cf-label">{t("Code")}</label><input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="cf-input" data-testid="menu-item-code" /></div>
            <div><label className="cf-label">{t("Sell Price (Rp)")}</label><input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="cf-input" data-testid="menu-item-price" /></div>
          </div>
          <div><label className="cf-label">{t("Category")}</label><input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="cf-input" placeholder={t("e.g. Main, Drink")} /></div>
        </div>
      </Modal>

      {/* Recipe editor modal */}
      <Modal open={!!recipeFor} onClose={() => setRecipeFor(null)} title={`${t("Recipe")} · ${recipeFor?.name || ""}`} size="lg"
        footer={canEdit ? <><Button variant="outline" onClick={() => setRecipeFor(null)}>{t("Cancel")}</Button><Button onClick={saveRecipe} loading={busy} data-testid="save-recipe">{t("Save Recipe")}</Button></> : <Button variant="outline" onClick={() => setRecipeFor(null)}>{t("Close")}</Button>}>
        {!recipe ? <p className="py-8 text-center text-sm text-ink-mute">{t("Loading…")}</p> : (
          <>
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm text-ink-soft">{t("Quantity of each ingredient used per")} <b>{t("1 unit sold")}</b>.</p>
              <button onClick={() => setAdvanced((a) => !a)} className="text-xs font-semibold text-primary" data-testid="toggle-advanced">{advanced ? t("Hide") : t("Show")} {t("yield/loss")}</button>
            </div>
            <div className="space-y-2" data-testid="recipe-lines">
              {lines.map((l, i) => {
                const p = products.find((x) => x.id === l.product_id);
                return (
                  <div key={i} className="rounded-xl border border-line p-2.5">
                    <div className="grid grid-cols-[minmax(0,1fr),5rem,1.75rem,1.5rem] items-center gap-2">
                      <Select value={l.product_id} onChange={(e) => setLines((d) => d.map((x, idx) => idx === i ? { ...x, product_id: e.target.value } : x))} className="min-w-0" data-testid={`recipe-product-${i}`}>
                        <option value="">{t("Select ingredient…")}</option>
                        {products.map((pp) => <option key={pp.id} value={pp.id}>{pp.name} ({pp.unit})</option>)}
                      </Select>
                      <input type="number" step="0.01" value={l.quantity} onChange={(e) => setLines((d) => d.map((x, idx) => idx === i ? { ...x, quantity: e.target.value } : x))} className="cf-input !text-right" placeholder={t("Qty")} data-testid={`recipe-qty-${i}`} />
                      <span className="truncate text-xs text-ink-mute">{p?.unit || ""}</span>
                      {canEdit && <button onClick={() => setLines((d) => d.filter((_, idx) => idx !== i))} className="text-danger" data-testid={`recipe-remove-${i}`}><X size={16} /></button>}
                    </div>
                    {advanced && (
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <div><label className="text-[11px] text-ink-mute">{t("Yield %")}</label><input type="number" value={l.yield_pct} onChange={(e) => setLines((d) => d.map((x, idx) => idx === i ? { ...x, yield_pct: e.target.value } : x))} className="cf-input" /></div>
                        <div><label className="text-[11px] text-ink-mute">{t("Prep loss %")}</label><input type="number" value={l.prep_loss_pct} onChange={(e) => setLines((d) => d.map((x, idx) => idx === i ? { ...x, prep_loss_pct: e.target.value } : x))} className="cf-input" /></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {canEdit && <Button size="sm" variant="outline" className="mt-2" onClick={() => setLines((d) => [...d, { product_id: "", quantity: "", yield_pct: 100, prep_loss_pct: 0 }])} data-testid="add-recipe-line"><Plus size={14} /> {t("Add ingredient")}</Button>}
            <div className="mt-4 flex items-center justify-between rounded-xl bg-app px-4 py-3">
              <div><p className="text-xs text-ink-mute">{t("Recipe cost")}</p><p className="font-heading text-lg font-bold text-ink" data-testid="recipe-cost">{fmtRp(recipeCost)}</p></div>
              <div className="text-right"><p className="text-xs text-ink-mute">{t("Margin vs price")} ({fmtRp(recipe.menu_price)})</p><p className={`font-heading text-lg font-bold ${(recipe.menu_price - recipeCost) >= 0 ? "text-success-text" : "text-danger-text"}`}>{fmtRp((recipe.menu_price || 0) - recipeCost)}</p></div>
            </div>
            <button onClick={loadVersions} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary" data-testid="show-versions"><History size={13} /> {t("Version history")}</button>
            {versions && (
              <div className="mt-2 space-y-1.5" data-testid="recipe-versions">
                {versions.map((v) => (
                  <div key={v.id} className="flex items-center justify-between rounded-lg border border-line px-3 py-1.5 text-xs">
                    <span className="font-semibold">v{v.version} <Badge tone={v.status === "active" ? "success" : "mute"}>{t(v.status)}</Badge></span>
                    <span className="text-ink-mute">{v.line_count} {t("ingredients")} · {t("from")} {(v.effective_from || "").slice(0, 10)}{v.effective_to ? ` ${t("to")} ${v.effective_to.slice(0, 10)}` : ""}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Modal>

      <MenuImportWizard open={importOpen} onClose={() => setImportOpen(false)} onDone={load} />
      <MenuImportHistory open={historyOpen} onClose={() => setHistoryOpen(false)} onChanged={load} />
    </div>
  );
}
